<template>
  <div class="map-container">
    <!-- 告警信息走马灯 -->
    <transition name="alert-fade">
      <div v-if="recentActiveAlerts && recentActiveAlerts.length > 0" class="alert-carousel-container">
        <el-carousel 
          height="36px"
          direction="horizontal"
          :autoplay="true"
          :interval="30000"
          indicator-position="none"
          arrow="never"
          class="alert-carousel"
          @change="onCarouselChange">
          <el-carousel-item v-for="alert in recentActiveAlerts" :key="`${alert.id}-${alert.playCount || 0}`">
            <div class="alert-message">
              <i class="el-icon-warning-outline"></i>
              <span>{{ getCarouselAlertMessage(alert) }}</span>
              <div class="alert-info">
                <i class="el-icon-close alert-close" @click.stop="removeAlert(alert)"></i>
              </div>
            </div>
          </el-carousel-item>
        </el-carousel>
      </div>
    </transition>
    
    <!-- 移除顶部导航栏 -->
    <div class="map-content">
      <base-map
        ref="baseMap"
        :initial-zoom="zoom"
        :initial-center="center"
        :map-options="mapOptions"
        :map-type="currentMapType"
        :show-measure-tool="true"
        @map-ready="onMapReady"
        @update:zoom="zoomUpdated"
        @update:center="centerUpdated"
        @mouse-move="onMouseMove"
        @fullscreen-change="onFullscreenChange">
        <!-- 使用插槽添加额外的地图元素 -->
        <template v-slot:map-overlays>
          <!-- 船舶图层由JS动态添加 -->
          
          <!-- 点标注组件 -->
          <point-markers
            :point-markers="pointMarkers">
          </point-markers>
        </template>
        
        <!-- 使用插槽添加图层管理按钮 -->
        <template v-slot:control-buttons>
          <!-- 图层管理按钮 -->
          <div class="map-control-icon" @click="toggleLayerManager">
            <i class="el-icon-s-operation"></i>
          </div>
          <!-- 功能按钮（仅管理员/值班室显示） -->
          <div v-if="canUseHeatmap" class="map-control-icon" @click="toggleHeatmapPanel">
            <i class="el-icon-menu"></i>
          </div>
          <!-- 返回实时船舶：仅在有叠加内容时显示 -->
          <div v-if="hasClearableOverlay" class="map-control-icon" title="返回实时船舶（清除会遇/激光射线/热力图）" @click="clearMapOverlays">
            <i class="el-icon-refresh-left"></i>
          </div>
        </template>
      </base-map>
      
      <!-- 添加船舶信息侧边面板 -->
      <ship-info-panel
        ref="shipInfoPanel"
        :visible="shipPanelVisible && !alertDetailVisible"
        :ship="currentShip"
        @close="closeShipPanel"
        @locate="locateShipById"
        @show-track="showShipTrackDetail"
        @show-warning="showShipWarning"
        @vessel-updated="onVesselUpdated">
      </ship-info-panel>
      
      <!-- 预警信息组件 - 使用新版WarningNoticePanel -->
      <warning-notice-panel
        ref="warningNoticePanel"
        :navigation-notices="navigationNotices"
        :vhf-enabled="vhfEnabled"
        :current-bridge-id="currentBridgeId"
        @toggle-vhf="toggleVhf"
        @remove-notice="removeNoticeFromPanel"
        @show-ship-track="showShipTrack"
        @show-ship-track-with-data="showShipTrackWithData"
        @show-meeting-tracks-with-data="showMeetingTracksWithData"
        @locate-ship="locateShipById"
        @draw-laser-ray="onDrawLaserRay"
        @draw-meeting="onDrawMeeting"
        @alert-handled="refreshAlertData"
        @show-notice-detail="showNoticeDetail"
        @clear-navigation-notices="clearNavigationNotices">
      </warning-notice-panel>
      
      <!-- 会遇双船航迹信息卡 -->
      <div v-if="meetingTracksInfo" class="meeting-tracks-card">
        <div class="mt-head">
          <i class="el-icon-guide"></i>
          <span>会遇双船航迹</span>
          <i class="el-icon-close mt-close" title="关闭（返回实时船舶）" @click="clearMapOverlays"></i>
        </div>
        <div class="mt-body">
          <div class="mt-row" v-for="(s, i) in meetingTracksInfo.ships" :key="i">
            <span class="mt-badge" :class="i === 0 ? 'mt-badge-a' : 'mt-badge-b'">{{ i === 0 ? 'A' : 'B' }}</span>
            <span class="mt-name">{{ s.name }}</span>
            <span class="mt-meta">点数 {{ s.count }}｜均速 {{ s.avgSpeed }} 节｜最高 {{ s.maxSpeed }} 节</span>
          </div>
          <div class="mt-time" v-if="meetingTracksInfo.timeRange">
            时间范围：{{ meetingTracksInfo.timeRange.startTime }} 至 {{ meetingTracksInfo.timeRange.endTime }}
          </div>
        </div>
      </div>

      <!-- 告警详情面板 -->
      <alert-detail-panel
        :visible.sync="alertDetailVisible"
        :alert-data="currentAlert"
        :bridge-id="currentBridgeId"
        :active-tab="alertActiveTab"
        :z-index="getPanelZIndex('alert')"
        @alert-handled="refreshAlertData"
        @alert-deleted="handleAlertDeleted"
        @update:active-tab="updateAlertActiveTab"
        @panel-focus="handlePanelFocus('alert')"
        @show-ship-track="showShipTrack"
        @show-ship-track-with-data="showShipTrackWithData"
      ></alert-detail-panel>

      
      
      <!-- 通航记录面板 -->
      <bridge-navigation-panel
        :visible.sync="trafficPanelVisible"
        :bridgeId="currentBridgeId"
        :z-index="getPanelZIndex('traffic')"
        @panel-focus="handlePanelFocus('traffic')"
        @show-ship-track="showShipTrack"
        @show-ship-track-with-data="showShipTrackWithData"
      />
      
      <!-- 偏航统计面板 -->
      <bridge-deviation-statistics
        :visible.sync="deviationStatisticsVisible"
        :bridge-id="currentBridgeId"
        :initial-active-tab="statisticsActiveTab"
        :z-index="getPanelZIndex('statistics')"
        @panel-focus="handlePanelFocus('statistics')"
      />
      
      <!-- VHF呼叫记录面板 -->
      <vhf-call-record-panel
        :visible.sync="vhfCallRecordVisible"
        :bridge-id="currentBridgeId"
        :z-index="getPanelZIndex('vhf')"
        @panel-focus="handlePanelFocus('vhf')"
        @show-ship-track-with-data="showShipTrackWithData"
      />
      
      <!-- 添加搜索工具栏 -->
      <ship-search 
        v-model="search"
        :is-searching="isSearching"
        v-show="!alertDetailVisible"
        @search="searchShip">
      </ship-search>
      
      <!-- 图层管理面板 -->
      <layer-manager
        v-show="layerManagerVisible" 
        ref="layerManager"
        :layer-status="layerStatus"
        :anti-overlap-enabled="antiOverlapEnabled"
        @switch-layer="switchLayer"
        @toggle-anti-overlap="toggleAntiOverlap"
        @close="layerManagerVisible = false">
      </layer-manager>

      <!-- 热力图功能面板（仅管理员/值班室显示） -->
      <heatmap-panel
        v-if="canUseHeatmap"
        v-show="heatmapPanelVisible"
        ref="heatmapPanel"
        :map="map"
        @close="heatmapPanelVisible = false"
        @track-layer-change="heatmapTrackActive = $event"
        @export-before="onHeatmapExportBefore"
        @export-after="onHeatmapExportAfter">
      </heatmap-panel>

      <!-- 桥梁预警规则弹窗 -->
      <bridge-alert-rules
        :visible.sync="bridgeAlertRulesVisible"
        :bridge-id="currentBridgeId"
        :active-tab="bridgeRulesActiveTab"
        :z-index="getPanelZIndex('bridgeRules')"
        @close="handleBridgeAlertRulesClose"
        @update:active-tab="updateBridgeRulesActiveTab"
        @panel-focus="handlePanelFocus('bridgeRules')"
      />
      
      <!-- 船舶轨迹查看器 -->
    </div>
    
    <!-- 雷达目标信息对话框 -->
    <el-dialog
      title="雷达目标信息"
      :visible.sync="radarDialogVisible"
      width="30%">
      <div v-if="currentRadarTarget" class="radar-popup">
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="目标ID">{{ currentRadarTarget.targetId }}</el-descriptions-item>
          <el-descriptions-item label="目标名称">{{ currentRadarTarget.targetName }}</el-descriptions-item>
          <el-descriptions-item v-if="currentRadarTarget.pos" label="经度">{{ currentRadarTarget.pos.longitude }}</el-descriptions-item>
          <el-descriptions-item v-if="currentRadarTarget.pos" label="纬度">{{ currentRadarTarget.pos.latitude }}</el-descriptions-item>
          <!-- <el-descriptions-item v-if="currentRadarTarget.course !== undefined" label="航向">{{ currentRadarTarget.course }}°</el-descriptions-item>
          <el-descriptions-item v-if="currentRadarTarget.speed !== undefined" label="航速">{{ currentRadarTarget.speed }} 节</el-descriptions-item> -->
          <el-descriptions-item v-if="currentRadarTarget.distance !== undefined" label="距离">{{ currentRadarTarget.distance }} 米</el-descriptions-item>
          <el-descriptions-item v-if="currentRadarTarget.bearing !== undefined" label="方位">{{ currentRadarTarget.bearing }}°</el-descriptions-item>
          <!-- <el-descriptions-item v-if="currentRadarTarget.size && currentRadarTarget.size.length" label="长度">{{ currentRadarTarget.size.length }} 米</el-descriptions-item>
          <el-descriptions-item v-if="currentRadarTarget.size && currentRadarTarget.size.width" label="宽度">{{ currentRadarTarget.size.width }} 米</el-descriptions-item> -->
          <!-- <el-descriptions-item label="数据源">{{ currentRadarTarget.dataSource || '雷达' }}</el-descriptions-item> -->
          <el-descriptions-item label="接收时间">{{ currentRadarTarget.receive_time }}</el-descriptions-item>
        </el-descriptions>
        <div class="dialog-footer" style="margin-top: 15px; text-align: center;">
          <el-button size="small" @click="closeRadarDialog">关闭</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 船舶航迹查看器 -->
    <ship-track-viewer
      v-if="showTrackViewer && map && currentShipMmsi"
      :key="currentShipMmsi"
      :visible="showTrackViewer"
      :mmsi="currentShipMmsi || ''"
      :map="map"
      :time-range="trackTimeRange && Array.isArray(trackTimeRange) && trackTimeRange.length === 2 ? trackTimeRange : undefined"
      :critical-areas="warningAreas || []"
      @close="closeTrackViewer"
      @update:visible="showTrackViewer = $event"
      @locate-ship="onTrackLocateShip"
      @before-destroy="onTrackViewerBeforeDestroy"
      ref="trackViewer">
    </ship-track-viewer>
  </div>
</template>

<script>
import BaseMap from '@/components/BaseMap.vue'
import ShipSearch from './ShipSearch.vue'
import WarningNoticePanel from './WarningNoticePanel.vue'
import LayerManager from './LayerManager.vue'
import HeatmapPanel from './HeatmapPanel.vue'
import ShipMapViewJs from './ShipMapView.js'
import ShipTrackViewer from './ShipTrackViewer.vue'
import ShipInfoPanel from './ShipInfoPanel.vue'
import PointMarkers from './PointMarkers.vue'
import L from 'leaflet'
// 导入告警数据接收器
import { alertDataReceiver } from '@/utils/AlertDataReceiver';
// 导入船名清洗工具（去除 AIS 报文中的 @ 填充符）
import { sanitizeVesselName } from '@/utils/AisDataReceiver';
// 导入权限工具（角色判断：管理员 ADMIN / 值班室 MONITOR）
import { getUserRoles, hasAdminAccess } from '@/utils/permissionUtils';
import AlertDetailPanel from './AlertDetailPanel.vue'
import BridgeNavigationPanel from './BridgeNavigationPanel.vue'
import BridgeDeviationStatistics from './BridgeDeviationStatistics.vue'
import VhfCallRecordPanel from './VhfCallRecordPanel.vue'
import BridgeAlertRules from '../bridge/BridgeAlertRules.vue'

export default {
  name: 'ShipMapView',
  components: {
    BaseMap,
    ShipSearch,
    WarningNoticePanel,
    LayerManager,
    HeatmapPanel,
    ShipTrackViewer,
    ShipInfoPanel,
    PointMarkers,
    AlertDetailPanel,
    BridgeNavigationPanel,
    BridgeDeviationStatistics,
    VhfCallRecordPanel,
    BridgeAlertRules
  },
  mixins: [ShipMapViewJs],
  computed: {
    /**
     * 热力图功能可见性：仅管理员(ADMIN)与值班室(MONITOR)可查看并控制
     * 通过 permissionVersion 建立响应式依赖，权限更新后自动重算
     */
    canUseHeatmap() {
      return this.permissionVersion >= 0 && this.hasHeatmapPermission();
    },
    /**
     * 是否存在需要“返回实时船舶”清除的叠加内容：
     * 会遇/激光射线图层、已生成的轨迹线图层（热力图）、会遇双船航迹、航迹查看器。
     * 注意：仅打开热力图面板（未生成轨迹线）时不显示该按钮。
     */
    hasClearableOverlay() {
      return !!this.overlayActive || !!this.heatmapTrackActive || !!this.showTrackViewer || !!this.meetingTracksInfo;
    },
    /**
     * 获取最近的3条活跃告警用于走马灯显示（仅实时数据）
     */
    recentActiveAlerts() {
      if (!this.activeAlerts || this.activeAlerts.length === 0) {
        return [];
      }
      
      // 确保只有实时数据才显示在走马灯中
      // 实时数据的特征：通过onAlertReceived方法添加的，包含playCount字段
      const realtimeAlerts = this.activeAlerts.filter(alert => {
        return Object.prototype.hasOwnProperty.call(alert, 'playCount') && this.getCarouselAlertMessage(alert);
      });
      
      console.log(`【走马灯数据】总告警数: ${this.activeAlerts.length}, 实时告警数: ${realtimeAlerts.length}`);
      
      // 按时间倒序排序，取最新的3条
      const result = realtimeAlerts
        .sort((a, b) => {
          const timeA = new Date(a.alertTime || a.time || 0).getTime();
          const timeB = new Date(b.alertTime || b.time || 0).getTime();
          return timeB - timeA; // 降序排列，最新的在前
        })
        .slice(0, 3); // 只取前3条
        
      // 添加详细的调试信息
      result.forEach((alert, index) => {
        const message = this.getCarouselAlertMessage(alert);
        console.log(`【走马灯显示】第${index + 1}条: ID=${alert.id}, playCount=${alert.playCount}, 消息="${message}"`);
      });
      
      return result;
    }
  },
  data() {
    return {
      // 权限更新版本号（用于热力图入口可见性重算）
      permissionVersion: 0,
      // 热力图导出前的船舶图层显示状态（导出后用于恢复）
      heatmapShipStateForExport: null,
      // 添加通航信息通知数据
      navigationNotices: [],
      alertDetailVisible: false,
      trafficPanelVisible: false,
      deviationStatisticsVisible: false, // 偏航统计面板可见性
      statisticsActiveTab: 'deviation', // 统计面板的活跃标签页
      vhfCallRecordVisible: false, // VHF呼叫记录面板可见性
      currentAlert: null,
      currentBridgeId: null, // 添加当前桥梁ID
      alertActiveTab: 'records', // 默认显示告警记录标签页
      activeAlerts: [], // 用于走马灯显示的活跃告警
      bridgeAlertRulesVisible: false, // 桥梁预警规则弹窗可见性
      // 弹窗层级管理
      panelZIndexBase: 2000, // 基础z-index值
      panelFocusOrder: [], // 面板焦点顺序，最后一个是最顶层的
      panelIds: ['alert', 'traffic', 'statistics', 'vhf', 'bridgeRules'], // 所有面板的ID
      // 告警清理定时器
      alertCleanupTimer: null,
      // 走马灯备用删除定时器（全局单个定时器）
      carouselBackupTimer: null,
      // 轨迹查询模式
      trackQueryMode: {
        useLatestRecords: true,
        maxRecords: 10
      },
      bridgeRulesActiveTab: 'rules', // 桥梁预警规则面板的活跃标签页
      // 是否存在地图叠加内容（会遇/激光射线），用于控制“返回实时船舶”按钮显隐
      overlayActive: false,
      // 热力图/轨迹线图层是否已生成（仅生成后才需要“返回实时船舶”按钮）
      heatmapTrackActive: false,
      // 会遇双船航迹信息（两船点数/速度统计），为空表示未展示
      meetingTracksInfo: null,
    };
  },
  created() {
    // 初始化告警数据接收器
    this.initAlertDataReceiver();
    
    // 尝试从localStorage中获取当前桥梁ID
    this.initCurrentBridgeId();
    
    // 监听桥梁变更事件
    this.$root.$on('bridge-changed', this.onBridgeChanged);

    // 监听权限更新事件（用于热力图入口可见性刷新）
    this.$root.$on('permissions-updated', this.onPermissionsUpdated);
    
    // 通过告警数据接收器的事件总线监听告警消息事件
    
    // 导入告警事件方法
    const { alertEvents } = require('@/utils/AlertDataReceiver');
    
    // 使用事件总线监听告警消息
    if (alertEvents) {
      alertEvents.onAlertDataUpdated(this.onAlertDataUpdated);
    }
  },
  mounted() {
    // 确保调用原始的mounted方法
    const originalMounted = ShipMapViewJs.mounted;
    if (originalMounted) {
      originalMounted.call(this);
    }
    
    // 如果恢复了多桥梁状态，需要加载所有桥梁的区域数据
    this.$nextTick(() => {
      if (this.selectedBridgeIds && this.selectedBridgeIds.length > 1) {
        console.log('【ShipMapView】检测到多桥梁状态，延迟加载区域数据，桥梁IDs:', this.selectedBridgeIds)
        // 延迟一段时间确保地图完全初始化
        setTimeout(() => {
          if (this.selectedBridgeIds && this.selectedBridgeIds.length > 1) {
            console.log('【ShipMapView】开始加载多桥梁区域数据:', this.selectedBridgeIds)
            this.loadMultipleBridgeAreas(this.selectedBridgeIds)
          }
        }, 1000) // 缩短延迟时间
      } else if (this.selectedBridgeIds && this.selectedBridgeIds.length === 1) {
        console.log('【ShipMapView】检测到单桥梁状态，桥梁ID:', this.selectedBridgeIds[0])
      } else {
        console.log('【ShipMapView】未检测到桥梁选择状态')
      }
    })
    
    // 启动告警清理定时器，每10秒检查一次
    this.startAlertCleanupTimer();
  },
  beforeDestroy() {
    // 移除桥梁变更事件监听
    this.$root.$off('bridge-changed', this.onBridgeChanged);

    // 移除权限更新事件监听
    this.$root.$off('permissions-updated', this.onPermissionsUpdated);
    
    // // 移除告警消息事件监听
    // console.warn('【告警监听】移除告警消息监听器');
    
    // 导入告警事件方法
    const { alertEvents } = require('@/utils/AlertDataReceiver');
    
    // 移除事件总线监听
    if (alertEvents) {
      alertEvents.offAlertDataUpdated(this.onAlertDataUpdated);
    }
    
    // 清理告警清理定时器
    this.stopAlertCleanupTimer();
    
    // 清理告警船舶闪烁重绘定时器
    if (this.alertBlinkTimer) {
      clearInterval(this.alertBlinkTimer);
      this.alertBlinkTimer = null;
    }
    
    // 清理所有告警的备用删除定时器
    this.clearAllBackupTimers();
  },
  methods: {
    /**
     * 权限更新事件回调：触发热力图入口可见性重新计算
     */
    onPermissionsUpdated() {
      this.permissionVersion++;
    },
    /**
     * 判断当前用户是否具备热力图权限（管理员 ADMIN / 值班室 MONITOR）
     */
    hasHeatmapPermission() {
      const roles = getUserRoles();
      if (Array.isArray(roles) && roles.length) {
        return roles.indexOf('ADMIN') !== -1 || roles.indexOf('MONITOR') !== -1;
      }
      // 兼容：无角色明细时以 hasAdminAccess(=ADMIN||MONITOR) 判定
      return hasAdminAccess();
    },
    /**
     * 热力图导出前：临时隐藏船舶图层，避免船舶图标出现在导出图片中
     */
    onHeatmapExportBefore() {
      this.heatmapShipStateForExport = this.layerStatus.ship;
      if (this.layerStatus.ship) {
        this.layerStatus.ship = false;
        this.toggleShipVisibility(false);
      }
    },
    /**
     * 热力图导出后：恢复船舶图层的显示状态
     */
    onHeatmapExportAfter() {
      if (this.heatmapShipStateForExport) {
        this.layerStatus.ship = true;
        this.toggleShipVisibility(true);
      }
      this.heatmapShipStateForExport = null;
    },
    /**
     * 初始化当前桥梁ID和多桥梁状态
     */
    initCurrentBridgeId() {
      // 首先尝试恢复多桥梁选择状态
      const multiBridgeRestored = this.restoreMultiBridgeSelection()
      
      if (multiBridgeRestored) {
        console.log('【ShipMapView】成功恢复多桥梁选择状态，桥梁数量:', this.selectedBridges.length)
        // 设置主桥梁ID
        if (this.selectedBridgeIds.length > 0) {
          this.currentBridgeId = this.selectedBridgeIds[0]
          console.log('【ShipMapView】设置主桥梁ID:', this.currentBridgeId)
        }
        return
      }
      
      // 如果多桥梁状态恢复失败，使用单桥梁恢复逻辑
      try {
        const savedBridgeJson = localStorage.getItem('selectedBridge');
        if (savedBridgeJson) {
          const savedBridge = JSON.parse(savedBridgeJson);
          if (savedBridge && savedBridge.id) {
            this.currentBridgeId = savedBridge.id;
            console.log('从localStorage初始化当前桥梁ID:', this.currentBridgeId);
            
            // 初始化单桥梁状态
            this.selectedBridges = [savedBridge]
            this.selectedBridgeIds = [savedBridge.id.toString()]
          }
        }
      } catch (e) {
        console.warn('从localStorage获取桥梁ID失败', e);
      }
    },
    
    /**
     * 从localStorage恢复多桥梁选择状态
     */
    restoreMultiBridgeSelection() {
      try {
        const savedState = localStorage.getItem('multiBridgeSelection')
        if (savedState) {
          const multiBridgeState = JSON.parse(savedState)
          
          // 验证数据有效性
          if (multiBridgeState.selectedBridges && Array.isArray(multiBridgeState.selectedBridges) &&
              multiBridgeState.selectedBridgeIds && Array.isArray(multiBridgeState.selectedBridgeIds)) {
            
            if (multiBridgeState.selectedBridges.length > 0) {
              this.selectedBridges = multiBridgeState.selectedBridges
              this.selectedBridgeIds = multiBridgeState.selectedBridgeIds.map(id => id.toString())
              
              console.log('【ShipMapView】恢复多桥梁选择状态:', {
                selectedBridges: this.selectedBridges.length,
                bridgeNames: this.selectedBridges.map(b => b.name),
                bridgeIds: this.selectedBridgeIds
              })
              
              return true
            }
          }
        }
      } catch (error) {
        console.error('【ShipMapView】恢复多桥梁选择状态失败:', error)
      }
      return false
    },
    
    /**
     * 处理桥梁变更事件
     */
    onBridgeChanged(bridge) {
      if (bridge && bridge.id) {
        this.currentBridgeId = bridge.id;
        console.log('桥梁变更，设置当前桥梁ID:', this.currentBridgeId);
      }
      
      // 注意：不再调用handleBridgeChange，避免重复处理
      // ShipMapView.js中已经注册了相同的事件监听器
      console.log('【ShipMapView.vue】桥梁变更事件已处理，区域加载由ShipMapView.js统一处理');
    },
    
    /**
     * 初始化告警数据接收器
     */
    async initAlertDataReceiver() {
      try {
        await alertDataReceiver.init(this);        
        await alertDataReceiver.connect();
        
      } catch (error) {
        console.error('【告警初始化】初始化告警数据接收器失败:', error);
      }
    },

    /**
     * 获取走马灯显示的告警消息（确保只处理实时数据）
     */
    getCarouselAlertMessage(alert) {
      // 走马灯只显示实时数据，使用实时数据的字段
      return alert.message || alert.description || alert.alertMessage;
    },

    /**
     * 同步告警船舶闪烁重绘定时器
     * 有未处理告警时启动定时器驱动Canvas重绘（红色闪烁），无告警时停止
     */
    syncAlertBlinkTimer() {
      const hasActiveAlerts = this.activeAlerts && this.activeAlerts.length > 0;
      if (hasActiveAlerts && !this.alertBlinkTimer) {
        this.alertBlinkTimer = setInterval(() => {
          if (this.canvasLayer && this.canvasLayer._redraw) {
            this.canvasLayer._redraw(true);
          }
        }, 500);
      } else if (!hasActiveAlerts && this.alertBlinkTimer) {
        clearInterval(this.alertBlinkTimer);
        this.alertBlinkTimer = null;
      }
    },

    /**
     * 处理接收到的告警消息
     */
    onAlertReceived(alert) {
      console.warn('【告警接收】ShipMapView接收到告警消息:', alert);
      
      if (!alert || !alert.id) {
        console.warn('【告警处理】接收到无效的告警消息，无法处理:', alert);
        return;
      }
      
      // 检查是否已有该告警，避免重复添加
      const index = this.activeAlerts.findIndex(item => item.id === alert.id);
      
      if (index !== -1) {
        // 已存在则更新，重置播报次数
        console.warn(`【告警处理】更新已存在的告警 ID: ${alert.id}`);
        alert.playCount = 0; // 重置播报次数
        alert.createdAt = Date.now(); // 重新设置创建时间
        this.activeAlerts.splice(index, 1, alert);
      } else {
        // 不存在则添加到开头（最新的在前），初始化播报次数
        console.warn(`【告警处理】添加新告警 ID: ${alert.id}`);
        alert.playCount = 0; // 初始化播报次数
        alert.createdAt = Date.now(); // 设置创建时间用于备用删除检查
        this.activeAlerts.unshift(alert);
        
        // 保持告警列表不超过5条（避免内存过度占用，走马灯只显示最新3条）
        if (this.activeAlerts.length > 5) {
          this.activeAlerts = this.activeAlerts.slice(0, 5);
          console.warn(`【告警处理】告警数量超过限制，已裁剪到5条`);
        }
      }
      
      console.warn(`【告警状态】当前告警总数: ${this.activeAlerts.length}，走马灯显示: ${Math.min(this.activeAlerts.length, 3)} 条`);
      
      // 告警状态变更后刷新船舶标记，使预警船舶图标视觉区分生效
      this.updateShipMarkers();
      
      // 同步闪烁重绘定时器（有未处理告警则启动Canvas红色闪烁）
      this.syncAlertBlinkTimer();
      
      // 修复：设置备用删除定时器（Element UI轮播组件在只有1个项目时不会触发change事件）
      this.ensureAlertDeletion();
    },

    /**
     * 确保告警能被正确删除（修复Element UI轮播组件单项不触发change事件的问题）
     * 使用单个全局定时器避免多定时器冲突
     */
    ensureAlertDeletion() {
      // 清除现有的备用定时器
      if (this.carouselBackupTimer) {
        clearTimeout(this.carouselBackupTimer);
        console.warn(`【备用定时器】清除现有的备用删除定时器`);
      }
      
      // 如果没有告警，无需设置定时器
      if (this.activeAlerts.length === 0) {
        return;
      }
      
      // 设置新的备用删除定时器：60秒后检查并删除过期告警
      this.carouselBackupTimer = setTimeout(() => {
        // 检查所有告警，删除应该被删除的告警
        const alertsToRemove = [];
        
        this.activeAlerts.forEach(alert => {
          if (!alert.createdAt) {
            // 如果告警没有创建时间，添加当前时间
            alert.createdAt = Date.now();
            return;
          }
          
          const alertAge = Date.now() - alert.createdAt;
          const maxAge = 60000; // 60秒
          
          if (alertAge >= maxAge) {
            alertsToRemove.push(alert);
          }
        });
        
        // 删除过期告警
        alertsToRemove.forEach(alert => {
          this.removeAlert(alert);
        });
        
        // 如果还有剩余告警，继续设置定时器
        if (this.activeAlerts.length > 0) {
          this.ensureAlertDeletion();
        } else {
          this.carouselBackupTimer = null;
        }
      }, 60000); // 60秒后执行检查
      
    },

    /**
     * 处理接收到的告警数据更新
     */
    onAlertDataUpdated(data) {
      console.warn('【告警接收】ShipMapView接收到告警数据更新:', data);
      
      if (!data || !data.alert|| !data.alert.type) {
        console.warn('【告警处理】接收到无效的告警数据，无法处理');
        return;
      }


      // 不处理穿越、进入、离开的告警
      if(data.alert.type === 'CROSSING' || data.alert.type === 'ENTRY' || data.alert.type === 'EXIT') {
        return;
      }
      
      // 处理接收到的告警
      this.onAlertReceived(data.alert);
    },

    /**
     * 移除告警
     */
    removeAlert(alert) {
      if (!alert || !alert.id) {
        console.warn('【告警移除】尝试移除无效的告警');
        return;
      }
      
      console.warn(`【告警移除】尝试移除告警 ID: ${alert.id}`);
      
      const index = this.activeAlerts.findIndex(item => item.id === alert.id);
      if (index !== -1) {
        this.activeAlerts.splice(index, 1);
        console.warn(`【告警移除】成功移除告警 ID: ${alert.id}，剩余告警数: ${this.activeAlerts.length}`);
        
        // 告警移除后刷新船舶标记，恢复预警船舶图标
        this.updateShipMarkers();
        
        // 同步闪烁重绘定时器（无未处理告警时停止Canvas红色闪烁）
        this.syncAlertBlinkTimer();
        
        // 如果移除后没有告警了，清理全局备用定时器
        if (this.activeAlerts.length === 0) {
          console.log('【告警状态】所有告警已清理完毕');
          if (this.carouselBackupTimer) {
            clearTimeout(this.carouselBackupTimer);
            this.carouselBackupTimer = null;
            console.warn(`【备用定时器】清理全局备用删除定时器`);
          }
        }
      } else {
        console.warn(`【告警移除】未找到要移除的告警 ID: ${alert.id}`);
      }
    },

    /**
     * 从告警面板移除通知（区分实时数据和API数据）
     */
    removeNoticeFromPanel(id) {
      console.warn(`【告警面板删除】尝试移除告警 ID: ${id}, 类型: ${typeof id}`);
      
      // 确保 ID 类型一致性
      const alertId = parseInt(id);
      
      // 1. 只从activeAlerts中移除实时告警（走马灯数据源）
      // API数据不应该在activeAlerts中，所以只删除真正存在的实时告警
      const alertIndex = this.activeAlerts.findIndex(item => parseInt(item.id) === alertId);
      if (alertIndex !== -1) {
        // 找到了，说明这是实时数据，从走马灯中移除
        this.activeAlerts.splice(alertIndex, 1);
        console.warn(`【告警面板删除】成功从走马灯移除实时告警 ID: ${alertId}`);
      } else {
        // 没找到，说明这是API数据，不需要从走马灯中移除
        console.warn(`【告警面板删除】告警 ID: ${alertId} 不在走马灯中（可能是API数据）`);
      }
      
      // 2. 从navigationNotices中移除（告警面板数据源）
      const navIndex = this.navigationNotices.findIndex(notice => parseInt(notice.id) === alertId);
      if (navIndex !== -1) {
        this.navigationNotices.splice(navIndex, 1);
        console.warn(`【告警面板删除】成功从navigationNotices移除告警 ID: ${alertId}`);
      } else {
        console.warn(`【告警面板删除】未在navigationNotices中找到告警 ID: ${alertId}`);
      }
      
      // 3. 调用WarningNoticePanel的删除方法，处理API数据的删除缓存
      if (this.removeNotice && typeof this.removeNotice === 'function') {
        this.removeNotice(id);
        console.warn(`【告警面板删除】调用原removeNotice方法处理告警 ID: ${id}`);
      }
      
      console.warn(`【告警面板删除】告警删除完成 ID: ${alertId}，剩余走马灯告警数: ${this.activeAlerts.length}，剩余面板告警数: ${this.navigationNotices.length}`);
    },

    /**
     * 清空通航信息通知
     */
    clearNavigationNotices() {
      this.navigationNotices = [];
    },

    /**
     * 刷新告警数据
     */
    refreshAlertData() {
      // 保存告警面板关闭前的状态
      const wasAlertVisible = this.alertDetailVisible;
      
      // 关闭告警面板
      this.alertDetailVisible = false;
      this.trafficPanelVisible = false;
      this.currentAlert = null;
      
      // 通知预警信息面板刷新数据
      this.refreshWarningNoticePanel();
      
      // 如果告警面板之前是可见的，现在恢复船舶信息面板的状态
      if (wasAlertVisible && this.currentShip) {
        // 恢复船舶信息面板
        this.shipPanelVisible = true;
      }
    },

    /**
     * 处理告警删除事件
     */
    handleAlertDeleted(alertId) {
      console.log('告警已删除:', alertId);
      
      // 关闭告警详情面板
      this.alertDetailVisible = false;
      this.currentAlert = null;
      
      // 通知预警信息面板处理删除事件
      this.notifyWarningPanelAlertDeleted(alertId);
      
      // 如果有当前船舶，恢复船舶信息面板
      if (this.currentShip) {
        this.shipPanelVisible = true;
      }
      
      this.$message.success('告警记录已删除');
    },

    /**
     * 刷新预警信息面板数据
     */
    refreshWarningNoticePanel() {
      // 通过ref调用预警信息面板的刷新方法
      if (this.$refs.warningNoticePanel) {
        this.$refs.warningNoticePanel.refreshHistoricalAlerts();
        // 同时调用处理事件的方法
        this.$refs.warningNoticePanel.handleExternalAlertHandled();
      }
    },

    /**
     * 通知预警信息面板告警已删除
     */
    notifyWarningPanelAlertDeleted(alertId) {
      if (this.$refs.warningNoticePanel) {
        this.$refs.warningNoticePanel.handleExternalAlertDeleted(alertId);
      }
    },

    /**
     * 显示告警详情
     */
    showAlertDetail(alertData) {
      this.currentAlert = alertData;
      this.alertDetailVisible = true;
      
      // 同时确保该告警也添加到走马灯显示列表中
      this.onAlertReceived(alertData);
    },

    /**
     * 显示通航记录详情
     */
    showTrafficDetail(bridgeId) {
      this.currentBridgeId = bridgeId;
      this.trafficPanelVisible = true;
    },
    
    /**
     * 显示通知详情
     * @param {Object} notice - 通知数据
     * @param {Object} options - 额外选项，如 { showDetailTab: true } 表示显示详情标签页
     */
    showNoticeDetail(notice, options = {}) {
      // 隐藏船舶信息面板
      this.shipPanelVisible = false;
      
      // 关闭轨迹查看器
      if (this.showTrackViewer) {
        this.closeTrackViewer();
      }
      
      // 构建告警数据
      const alertData = {
        id: notice.id,
        alertTime: notice.time,
        alertLevel: notice.level,
        alertMessage: notice.message || notice.description,
        sourceType: notice.sourceType || 'AIS',
        sourceId: notice.mmsi,
        sourceName: notice.shipName,
        ruleType: notice.type,
        status: notice.status || 0
      };
      
      // 设置当前告警数据
      this.currentAlert = alertData;
      
      // 如果options中指定了显示详情标签页，则切换到详情标签
      if (options && options.showDetailTab) {
        this.alertActiveTab = 'detail';
      }
      
      // 显示告警详情面板
      this.alertDetailVisible = true;
      
      // 同时确保该告警也添加到走马灯显示列表中
      this.onAlertReceived(alertData);
    },
    
    /**
     * 更新告警活动标签页
     */
    updateAlertActiveTab(tab) {
      this.alertActiveTab = tab;
    },

    /**
     * 获取面板的z-index值
     */
    getPanelZIndex(panelId) {
      const index = this.panelFocusOrder.indexOf(panelId);
      if (index === -1) {
        // 面板不在焦点顺序中，返回基础z-index
        return this.panelZIndexBase;
      }
      // 返回基础z-index + 在焦点顺序中的位置
      return this.panelZIndexBase + index;
    },
    
    /**
     * 处理面板焦点事件
     */
    handlePanelFocus(panelId) {
      console.log('面板获得焦点:', panelId);
      
      // 从当前焦点顺序中移除该面板（如果存在）
      const currentIndex = this.panelFocusOrder.indexOf(panelId);
      if (currentIndex !== -1) {
        this.panelFocusOrder.splice(currentIndex, 1);
      }
      
      // 将该面板添加到焦点顺序的末尾（最顶层）
      this.panelFocusOrder.push(panelId);
      
      console.log('当前面板焦点顺序:', this.panelFocusOrder);
    },
    
    /**
     * 当面板显示时，自动将其置顶
     */
    bringPanelToFront(panelId) {
      this.handlePanelFocus(panelId);
    },
    
    /**
     * 处理船舶信息更新
     */
    onVesselUpdated(updatedVessel) {
      console.log('船舶信息已更新:', updatedVessel);
      
      // 如果当前有选中的船舶，更新它的信息
      if (this.currentShip && this.currentShip.userid === updatedVessel.mmsi) {
        // 保留当前船舶的位置和动态信息，更新其他静态信息
        const updatedShip = {
          ...this.currentShip,
          cnName: sanitizeVesselName(updatedVessel.cnName),
          shipname: sanitizeVesselName(updatedVessel.vesselName),
          shipType: updatedVessel.vesselType,
          callSign: updatedVessel.callSign,
          imo: updatedVessel.imo,
          flag: updatedVessel.flag,
          length: updatedVessel.length,
          width: updatedVessel.width,
          depth: updatedVessel.depth,
          load: updatedVessel.load,
          grossTonnage: updatedVessel.grossTonnage,
          buildYear: updatedVessel.buildYear,
          contacts: updatedVessel.contacts,
          contactNumber: updatedVessel.contactNumber,
          remark: updatedVessel.remark
        };
        
        // 更新当前船舶数据
        this.currentShip = updatedShip;
        
        // 如果有船舶管理实例，也更新其中的数据
        if (this.shipManager) {
          this.shipManager.updateShipInfo(updatedVessel.mmsi, updatedShip);
        }

        // 更新船舶图层
        // this.updateShipMarkers()
        
        this._shipUpdateTimeout = setTimeout(() => {
            // console.log('更新船舶标记，数量:', this.ships.length);
            this.updateShipMarkers();
            this._shipUpdateTimeout = null;
          }, 300);
      }
    },

    /**
     * 绘制激光射线（超高告警审核用）：
     * 在地图上画"激光点 → 遮挡点"的完整几何线段，并（若有方位/扇形半角）画出扇形两侧边界，
     * 便于目视确认该光束是否覆盖到该遮挡点。再次点击会先清除上一条。
     */
    onDrawLaserRay(p) {
      if (!p) return;
      if (!this.map) {
        this.$message.warning('地图尚未就绪，无法绘制');
        return;
      }
      // 清除上一次绘制的射线
      if (this._laserRayLayer && this.map.hasLayer(this._laserRayLayer)) {
        this.map.removeLayer(this._laserRayLayer);
      }
      this._laserRayLayer = null;
      this.rememberViewBeforeOverlay();

      const d = (p.distance != null && p.distance > 0) ? Number(p.distance) : 0;
      const half = (p.sectorHalfAngleDeg != null && p.sectorHalfAngleDeg > 0) ? Number(p.sectorHalfAngleDeg) : 0;
      const az = (p.azimuthDeg != null) ? Number(p.azimuthDeg) : null;

      const group = L.featureGroup();
      const origin = [p.deviceLat, p.deviceLon];

      // 4) 检测线段：激光点 → 遮挡点（蓝色实线）
      group.addLayer(L.polyline([origin, [p.obsLat, p.obsLon]], {
        color: '#2d8cf0', weight: 4, opacity: 0.95
      }).bindTooltip('激光→遮挡点' + (d > 0 ? ' ' + d.toFixed(1) + 'm' : '')));

      // 5) 指北角方向：黄虚线，叠在蓝线之上；并延伸到测距点之外，确保可见
      if (az != null) {
        const azLen = d > 0 ? d * 1.5 : 800;
        const azPt = this.destPoint(p.deviceLat, p.deviceLon, az, azLen);
        group.addLayer(L.polyline([origin, [azPt.lat, azPt.lon]], {
          color: '#ffd21e', weight: 2, dashArray: '3,9', opacity: 1
        }).bindTooltip('指北角 ' + az + '°'));
        if (half > 0 && d > 0) {
          const sLen = d * 1.3;
          [az - half, az + half].forEach(b => {
            const pt = this.destPoint(p.deviceLat, p.deviceLon, b, sLen);
            group.addLayer(L.polyline([origin, [pt.lat, pt.lon]], {
              color: '#ffd21e', weight: 1, dashArray: '3,9', opacity: 0.55
            }));
          });
        }
      }

      // 3) 遮挡点 → 命中船 的连线（紫色虚线），并标注两者间距
      if (p.vesselLat != null && p.vesselLon != null) {
        const gap = this.calcDistM(p.obsLat, p.obsLon, p.vesselLat, p.vesselLon);
        group.addLayer(L.polyline([[p.obsLat, p.obsLon], [p.vesselLat, p.vesselLon]], {
          color: '#7b1fa2', weight: 2, dashArray: '2,6'
        }).bindTooltip('遮挡点↔船 约 ' + Math.round(gap) + 'm'));
        group.addLayer(L.circleMarker([p.vesselLat, p.vesselLon], {
          radius: 7, color: '#fff', weight: 2, fillColor: '#7b1fa2', fillOpacity: 1
        }).bindTooltip('命中船 ' + (p.vesselName || '')));
      }

      // 端点
      group.addLayer(L.circleMarker(origin, {
        radius: 7, color: '#fff', weight: 2, fillColor: '#2d8cf0', fillOpacity: 1
      }).bindTooltip('激光：' + (p.deviceName || '') + (az != null ? '，指北角 ' + az + '°' : '')));
      group.addLayer(L.circleMarker([p.obsLat, p.obsLon], {
        radius: 7, color: '#fff', weight: 2, fillColor: '#2d8cf0', fillOpacity: 0.55
      }).bindTooltip('遮挡点'));

      group.addTo(this.map);
      this._laserRayLayer = group;
      this.overlayActive = true;
      try {
        this.map.fitBounds(group.getBounds(), { padding: [60, 60], maxZoom: 18 });
      } catch (e) {
        console.warn('fitBounds 失败:', e);
      }
    },

    /**
     * 会遇“查看航迹”：把两船的 AIS 航迹同时画到地图上（A 红 / B 蓝），
     * 并叠加会遇线与两船船位，右侧信息卡展示两船点数与速度统计。
     *
     * @param {Object} p { ships: [{mmsi, name, list}], meetingLine, distance, timeRange }
     */
    showMeetingTracksWithData(p) {
      if (!this.map) {
        this.$message.warning('地图尚未就绪，无法显示航迹');
        return;
      }
      if (!p || !Array.isArray(p.ships) || p.ships.length === 0) {
        this.$message.warning('未获取到会遇船舶航迹数据');
        return;
      }
      // 关闭单船航迹查看器，避免与双船航迹图层相互冲突
      if (this.showTrackViewer) {
        this.closeTrackViewer();
      }
      // 清除上一次的双船航迹图层
      if (this._meetingTrackLayer && this.map.hasLayer(this._meetingTrackLayer)) {
        this.map.removeLayer(this._meetingTrackLayer);
      }
      this._meetingTrackLayer = null;
      // 同时移除“会遇”图标绘制的叠加，避免重复
      if (this._meetingLayer && this.map.hasLayer(this._meetingLayer)) {
        this.map.removeLayer(this._meetingLayer);
      }
      this._meetingLayer = null;

      this.rememberViewBeforeOverlay();

      const colors = ['#e63946', '#1e88e5']; // 船舶A 红色 / 船舶B 蓝色
      const group = L.featureGroup();
      const infos = [];
      const lastPositions = [];

      p.ships.forEach((ship, idx) => {
        const color = colors[idx] || '#333';
        const raw = Array.isArray(ship.list) ? ship.list.slice() : [];
        raw.sort((a, b) => {
          const ta = new Date(a.dataTime || a.positionTime || a.createTime || a.time || 0).getTime();
          const tb = new Date(b.dataTime || b.positionTime || b.createTime || b.time || 0).getTime();
          return ta - tb;
        });
        const latlngs = raw
          .filter(pt => pt && pt.latitude != null && pt.longitude != null)
          .map(pt => [Number(pt.latitude), Number(pt.longitude)]);
        const shipName = ship.name || ship.mmsi || ('船舶' + (idx + 1));

        if (latlngs.length >= 2) {
          group.addLayer(L.polyline(latlngs, {
            color, weight: 3, opacity: 0.9
          }).bindTooltip(shipName + ' 航迹（' + latlngs.length + '点）'));
        }
        if (latlngs.length >= 1) {
          const last = latlngs[latlngs.length - 1];
          const lastPoint = raw[raw.length - 1] || {};
          lastPositions.push(last);
          const label = (idx === 0 ? 'A｜' : 'B｜') + shipName;
          group.addLayer(L.marker(last, {
            icon: this.buildMeetingShipIcon(color, lastPoint.course, label),
            zIndexOffset: 1200
          }).bindTooltip(shipName));
        }

        const speeds = raw
          .map(pt => Number(pt.speed))
          .filter(v => !isNaN(v));
        const avg = speeds.length ? speeds.reduce((a, b) => a + b, 0) / speeds.length : null;
        const max = speeds.length ? Math.max.apply(null, speeds) : null;
        infos.push({
          name: shipName,
          mmsi: ship.mmsi,
          count: latlngs.length,
          avgSpeed: avg != null ? avg.toFixed(2) : '-',
          maxSpeed: max != null ? max.toFixed(2) : '-'
        });
      });

      // 会遇线（虚线）：优先使用告警携带的会遇线
      const line = Array.isArray(p.meetingLine) ? p.meetingLine : [];
      let meetLatLngs = line
        .filter(pt => Array.isArray(pt) && pt.length === 2 && pt[0] != null && pt[1] != null)
        .map(pt => [pt[1], pt[0]]);
      if (meetLatLngs.length < 2 && lastPositions.length >= 2) {
        meetLatLngs = [lastPositions[0], lastPositions[1]];
      }
      if (meetLatLngs.length >= 2) {
        const distText = p.distance != null ? ' ' + Math.round(p.distance) + 'm' : '';
        group.addLayer(L.polyline(meetLatLngs, {
          color: '#ff6d00', weight: 4, dashArray: '10,6', opacity: 1
        }).bindTooltip('会遇线' + distText));
        const midLat = (meetLatLngs[0][0] + meetLatLngs[1][0]) / 2;
        const midLon = (meetLatLngs[0][1] + meetLatLngs[1][1]) / 2;
        group.addLayer(L.marker([midLat, midLon], {
          interactive: false,
          icon: L.divIcon({
            className: 'meeting-line-label',
            html: '<span style="white-space:nowrap;background:#ff6d00;color:#fff;font-size:11px;' +
              'line-height:16px;padding:1px 6px;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,.4);">' +
              '会遇线' + distText + '</span>',
            iconSize: [0, 0],
            iconAnchor: [0, 0]
          })
        }));
      }

      group.addTo(this.map);
      if (typeof group.setZIndex === 'function') {
        group.setZIndex(900);
      }
      this._meetingTrackLayer = group;
      this.overlayActive = true;
      this.meetingTracksInfo = { ships: infos, timeRange: p.timeRange || null, distance: p.distance };

      try {
        this.map.fitBounds(group.getBounds(), { padding: [60, 60], maxZoom: 18 });
      } catch (e) {
        console.warn('fitBounds 失败:', e);
      }
      this.$message.success('已显示会遇两船航迹');
    },

    /**
     * 记录叠加绘制前的地图视野，便于“返回实时船舶”时还原（仅在无记录时保存，避免被二次点击覆盖）。
     */
    rememberViewBeforeOverlay() {
      if (!this.map || this._viewBeforeOverlay) {
        return;
      }
      try {
        const c = this.map.getCenter();
        this._viewBeforeOverlay = { center: [c.lat, c.lng], zoom: this.map.getZoom() };
      } catch (e) {
        console.warn('记录地图视野失败:', e);
      }
    },

    /**
     * 返回实时船舶：清除地图上的叠加内容（会遇航迹与会遇线、超高激光射线、热力图轨迹线），
     * 关闭航迹查看器/热力图面板，恢复实时船舶图层，并还原叠加前的视野。
     */
    clearMapOverlays() {
      if (!this.map) {
        this.$message.warning('地图尚未就绪');
        return;
      }
      let cleared = false;

      // 1) 会遇图层（两船航迹 + 会遇线）
      if (this._meetingLayer) {
        if (this.map.hasLayer(this._meetingLayer)) {
          this.map.removeLayer(this._meetingLayer);
        }
        this._meetingLayer = null;
        cleared = true;
      }

      // 2) 超高激光射线图层
      if (this._laserRayLayer) {
        if (this.map.hasLayer(this._laserRayLayer)) {
          this.map.removeLayer(this._laserRayLayer);
        }
        this._laserRayLayer = null;
        cleared = true;
      }

      // 3) 会遇双船航迹图层与信息卡
      if (this._meetingTrackLayer) {
        if (this.map.hasLayer(this._meetingTrackLayer)) {
          this.map.removeLayer(this._meetingTrackLayer);
        }
        this._meetingTrackLayer = null;
        cleared = true;
      }
      if (this.meetingTracksInfo) {
        this.meetingTracksInfo = null;
        cleared = true;
      }

      // 4) 热力图/轨迹线图层与面板
      const heatmap = this.$refs.heatmapPanel;
      if (heatmap && typeof heatmap.clearHeatmap === 'function') {
        heatmap.clearHeatmap();
        cleared = true;
      }
      if (this.heatmapTrackActive) {
        this.heatmapTrackActive = false;
        cleared = true;
      }
      if (this.heatmapPanelVisible) {
        this.heatmapPanelVisible = false;
        cleared = true;
      }

      // 4) 航迹查看器（展示航迹时会隐藏船舶图层）→ 关闭并恢复
      if (this.showTrackViewer) {
        this.closeTrackViewer();
        cleared = true;
      }

      // 5) 还原叠加前的视野
      if (this._viewBeforeOverlay) {
        try {
          this.map.setView(this._viewBeforeOverlay.center, this._viewBeforeOverlay.zoom);
        } catch (e) {
          console.warn('恢复地图视野失败:', e);
        }
        this._viewBeforeOverlay = null;
      }

      // 6) 强制恢复“实时船舶图层”
      // 不依赖 layerStatus.ship 的当前值（避免叠加过程中状态不一致导致船舶不再显示），
      // 直接重建标记并按最新船舶数据重绘。
      try {
        this.showTrackViewer = false;
        this.layerStatus.ship = true;
        if (!this.shipIcon && typeof this.createShipIcon === 'function') {
          this.createShipIcon();
        }
        if (this.layerInitStatus && !this.layerInitStatus.canvasInitialized && typeof this.initCanvasLayer === 'function') {
          this.initCanvasLayer();
        }
        // 重建标记（隐藏期间 canvas 内的标记可能已失效）
        this.shipMarkers = [];
        this.updateShipMarkers();
        if (this.canvasLayer) {
          if (this.shipMarkers && this.shipMarkers.length > 0 && this.canvasLayer.addLayers) {
            try {
              this.canvasLayer.addLayers(this.shipMarkers);
            } catch (e) {
              console.warn('重新添加船舶标记失败:', e);
            }
          }
          if (!this.map.hasLayer(this.canvasLayer)) {
            this.map.addLayer(this.canvasLayer);
          }
          if (typeof this.canvasLayer._redraw === 'function') {
            this.canvasLayer._redraw(true);
          }
        }
        cleared = true;
      } catch (e) {
        console.warn('恢复实时船舶图层失败:', e);
      }

      // 7) 隐藏“返回实时船舶”按钮
      this.overlayActive = false;

      if (cleared) {
        this.$message.success('已返回实时船舶显示');
      } else {
        this.$message.info('当前没有需要清除的叠加内容');
      }
    },

    /**
     * 绘制会遇航迹与会遇线（会遇告警目视审核用）：
     * 在地图上把"会遇"两船的近期航迹分别画成折线，并用虚线画出会遇线（两船当前位置连线），
     * 便于值班员目视确认两船是否相向而行。再次点击会先清除上一条。
     */
    onDrawMeeting(p) {
      if (!p || !p.ships || p.ships.length < 2) return;
      if (!this.map) {
        this.$message.warning('地图尚未就绪，无法绘制');
        return;
      }
      // 清除上一次绘制的会遇图层
      if (this._meetingLayer && this.map.hasLayer(this._meetingLayer)) {
        this.map.removeLayer(this._meetingLayer);
      }
      this._meetingLayer = null;
      this.rememberViewBeforeOverlay();

      const colors = ['#e63946', '#1e88e5']; // 船舶A 红色 / 船舶B 蓝色
      const group = L.featureGroup();

      p.ships.forEach((ship, idx) => {
        const color = colors[idx] || '#333';
        const course = ship.course != null ? Number(ship.course) : null;
        const track = Array.isArray(ship.track) ? ship.track : [];
        const latlngs = track
          .filter(pt => Array.isArray(pt) && pt.length === 2 && pt[0] != null && pt[1] != null)
          .map(pt => [pt[1], pt[0]]); // [lon, lat] -> [lat, lon]
        if (latlngs.length >= 2) {
          group.addLayer(L.polyline(latlngs, {
            color, weight: 2, opacity: 0.55
          }).bindTooltip((ship.name || ship.mmsi || ('船舶' + (idx + 1))) + ' 航迹'));
        }
        if (ship.latitude != null && ship.longitude != null) {
          const name = ship.name || ship.mmsi || ('船舶' + (idx + 1));
          const speedText = ship.speed != null ? Number(ship.speed).toFixed(1) + '节' : '';
          const label = (idx === 0 ? 'A｜' : 'B｜') + name + (speedText ? ' ' + speedText : '');
          group.addLayer(L.marker([ship.latitude, ship.longitude], {
            icon: this.buildMeetingShipIcon(color, course, label),
            zIndexOffset: 1200
          }).bindTooltip(name +
            (course != null ? '｜航向 ' + Math.round(course) + '°' : '') +
            (speedText ? '｜' + speedText : '')));
        }
      });

      // 会遇线：两船最近会遇点连线（橙色虚线，突出显示）
      const line = Array.isArray(p.meetingLine) ? p.meetingLine : [];
      const meetLatLngs = line
        .filter(pt => Array.isArray(pt) && pt.length === 2 && pt[0] != null && pt[1] != null)
        .map(pt => [pt[1], pt[0]]);
      if (meetLatLngs.length >= 2) {
        const distText = p.distance != null ? '相距约 ' + Math.round(p.distance) + ' m' : '';
        group.addLayer(L.polyline(meetLatLngs, {
          color: '#ff6d00', weight: 4, dashArray: '10,6', opacity: 1
        }).bindTooltip('会遇线 ' + distText));
        // 会遇线中点标注
        const midLat = (meetLatLngs[0][0] + meetLatLngs[1][0]) / 2;
        const midLon = (meetLatLngs[0][1] + meetLatLngs[1][1]) / 2;
        group.addLayer(L.marker([midLat, midLon], {
          interactive: false,
          icon: L.divIcon({
            className: 'meeting-line-label',
            html: '<span style="white-space:nowrap;background:#ff6d00;color:#fff;font-size:11px;' +
              'line-height:16px;padding:1px 6px;border-radius:8px;box-shadow:0 1px 3px rgba(0,0,0,.4);">' +
              '会遇线' + (p.distance != null ? ' ' + Math.round(p.distance) + 'm' : '') + '</span>',
            iconSize: [0, 0],
            iconAnchor: [0, 0]
          })
        }));
      }

      group.addTo(this.map);
      this._meetingLayer = group;
      this.overlayActive = true;
      try {
        this.map.fitBounds(group.getBounds(), { padding: [60, 60], maxZoom: 18 });
      } catch (e) {
        console.warn('fitBounds 失败:', e);
      }
    },

    /**
     * 构建会遇船舶图标：按航向旋转的船形箭头 + 名称/航速标签（无需依赖实时船舶图层即可看到两条船）
     */
    buildMeetingShipIcon(color, courseDeg, label) {
      const rot = courseDeg != null && !isNaN(courseDeg) ? courseDeg : 0;
      const safe = String(label == null ? '' : label)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      const html =
        '<div style="position:relative;width:28px;height:28px;">' +
          '<svg width="28" height="28" viewBox="0 0 24 24" ' +
            'style="transform:rotate(' + rot + 'deg);transform-origin:50% 50%;' +
            'filter:drop-shadow(0 0 2px rgba(0,0,0,0.7));">' +
            '<path d="M12 2 L19.5 21 L12 17.2 L4.5 21 Z" fill="' + color + '" ' +
              'stroke="#ffffff" stroke-width="1.2"/>' +
          '</svg>' +
          '<span style="position:absolute;left:26px;top:-3px;white-space:nowrap;' +
            'background:rgba(0,0,0,0.72);color:#fff;font-size:11px;line-height:16px;' +
            'padding:0 5px;border-radius:3px;">' + safe + '</span>' +
        '</div>';
      return L.divIcon({
        className: 'meeting-ship-icon',
        html: html,
        iconSize: [28, 28],
        iconAnchor: [14, 14]
      });
    },

    /**
     * 两点间距离（米）
     */
    calcDistM(lat1, lon1, lat2, lon2) {
      const R = 6371000;
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },

    /**
     * 以 (lat, lon) 为起点、沿 bearingDeg 方向前进 distM 米后的经纬度
     */
    destPoint(lat, lon, bearingDeg, distM) {
      const R = 6371000;
      const br = bearingDeg * Math.PI / 180;
      const lat1 = lat * Math.PI / 180;
      const lon1 = lon * Math.PI / 180;
      const d = distM / R;
      const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(br));
      const lon2 = lon1 + Math.atan2(Math.sin(br) * Math.sin(d) * Math.cos(lat1),
        Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
      return { lat: lat2 * 180 / Math.PI, lon: ((lon2 * 180 / Math.PI + 540) % 360) - 180 };
    },

    /**
     * 显示船舶轨迹
     * @param {string} mmsi - 船舶MMSI
     * @param {Object} timeRangeData - 时间范围数据（可选）
     */
    showShipTrack(mmsi, timeRangeData = null) {
      console.log('ShipMapView: 显示船舶轨迹:', mmsi, '时间范围数据:', timeRangeData);
      
      // 调用mixin中的showShipTrack方法
      if (this.$options.mixins && this.$options.mixins.length > 0) {
        const shipMapViewMixin = this.$options.mixins.find(mixin => mixin.name === 'default');
        if (shipMapViewMixin && shipMapViewMixin.methods && shipMapViewMixin.methods.showShipTrack) {
          return shipMapViewMixin.methods.showShipTrack.call(this, mmsi, timeRangeData);
        }
      }
      
      // 如果mixin方法不可用，使用默认实现
      if (!mmsi || mmsi.trim() === '') {
        this.$message.warning('船舶MMSI无效，无法查看轨迹');
        return;
      }
      
      // 设置时间范围
      if (timeRangeData && timeRangeData.startTime && timeRangeData.endTime) {
        // 使用传入的时间范围
        const startTime = new Date(timeRangeData.startTime);
        const endTime = new Date(timeRangeData.endTime);
        
        this.trackTimeRange = [
          this.formatDateTime(startTime),
          this.formatDateTime(endTime)
        ];
        
        // 保存查询模式信息
        this.trackQueryMode = {
          useLatestRecords: timeRangeData.useLatestRecords || false,
          maxRecords: timeRangeData.maxRecords || 0
        };
        
        console.log('使用传入的时间范围:', {
          startTime: timeRangeData.startTime,
          endTime: timeRangeData.endTime,
          formattedRange: this.trackTimeRange,
          queryMode: this.trackQueryMode
        });
        
        // 验证时间范围顺序
        console.log('时间范围数组验证:', {
          trackTimeRange: this.trackTimeRange,
          'trackTimeRange[0] (startTime)': this.trackTimeRange[0],
          'trackTimeRange[1] (endTime)': this.trackTimeRange[1]
        });
      } else {
        // 预设航迹时间范围为最近24小时，取最后10条数据
        const endTime = new Date();
        const startTime = new Date(endTime.getTime() - 24 * 60 * 60 * 1000); // 24小时前
        
        this.trackTimeRange = [
          this.formatDateTime(startTime),
          this.formatDateTime(endTime)
        ];
        
        // 默认查询模式
        this.trackQueryMode = {
          useLatestRecords: true,
          maxRecords: 10
        };
        
        console.log('使用默认时间范围（最近24小时，最后10条）:', this.trackTimeRange);
      }
      
      // 设置当前船舶MMSI
      this.currentShipMmsi = String(mmsi);
      
      // 尝试通过MMSI查找船舶信息用于显示船舶名称等
      const ship = this.ships.find(ship => ship.userid === mmsi);
      if (ship) {
        this.currentShip = ship;
        console.log('找到船舶信息，设置为当前船舶:', ship.shipname || ship.userid);
      } else {
        // 即使没有找到船舶信息，也创建一个基本的船舶对象
        this.currentShip = {
          userid: mmsi,
          shipname: mmsi,
          pos: null
        };
        console.log('未找到船舶信息，使用MMSI创建基本船舶对象');
      }
      
      // 保存当前船舶图层状态并隐藏船舶图层
      if (this.shipLayerStateBeforeTrack === null) {
        this.shipLayerStateBeforeTrack = this.layerStatus.ship;
        console.log('保存显示航迹前的船舶图层状态:', this.shipLayerStateBeforeTrack);
        
        // 如果船舶图层当前是显示状态，则隐藏它
        if (this.layerStatus.ship) {
          this.layerStatus.ship = false;
          this.toggleShipVisibility(false);
          console.log('隐藏船舶图层以显示航迹');
        }
      }
      
      // 显示航迹查看器
      this.showTrackViewer = true;
      
      const modeText = this.trackQueryMode.useLatestRecords ? 
        `最后${this.trackQueryMode.maxRecords}条记录` : '时间范围内所有记录';
      console.log(`开始显示船舶 ${mmsi} 的轨迹数据，时间范围: ${this.trackTimeRange[0]} 至 ${this.trackTimeRange[1]}，查询模式: ${modeText}`);
    },
    
    /**
     * 显示船舶轨迹（带数据）
     * @param {string} mmsi - 船舶MMSI
     * @param {Object} data - 包含轨迹数据的对象
     */
    showShipTrackWithData(mmsi, data) {
      console.log('ShipMapView: 接收到轨迹数据，开始处理:', {
        mmsi: mmsi,
        data: data
      });
      
      if (!mmsi || !data || !data.trajectoryData) {
        this.$message.error('轨迹数据无效，无法显示');
        return;
      }
      
      // 获取原始轨迹数据
      const originalTrajectoryData = data.trajectoryData.list || [];
      console.log('原始轨迹数据条数:', originalTrajectoryData.length);
      
      if (originalTrajectoryData.length === 0) {
        this.$message.warning('未找到轨迹数据');
        return;
      }
      
      // 按时间戳排序，取最后10条数据
      const sortedData = originalTrajectoryData.sort((a, b) => {
        return new Date(a.timestamp) - new Date(b.timestamp);
      });
      
      const latestData = sortedData.slice(-10);
      console.log('取最后10条轨迹数据:', latestData.length);
      
      // 设置当前船舶MMSI
      this.currentShipMmsi = String(mmsi);
      
      // 设置时间范围（用于显示）
      if (data.timeRange) {
        this.trackTimeRange = [
          data.timeRange.startTime,
          data.timeRange.endTime
        ];
      }
      
      // 设置查询模式
      this.trackQueryMode = {
        useLatestRecords: data.useLatestRecords || false,
        maxRecords: data.maxRecords || 10
      };
      
      // 尝试通过MMSI查找船舶信息
      const ship = this.ships.find(ship => ship.userid === mmsi);
      if (ship) {
        this.currentShip = ship;
        console.log('找到船舶信息，设置为当前船舶:', ship.shipname || ship.userid);
      } else {
        // 创建基本船舶对象
        this.currentShip = {
          userid: mmsi,
          shipname: mmsi,
          pos: null
        };
        console.log('未找到船舶信息，使用MMSI创建基本船舶对象');
      }
      
      // 保存当前船舶图层状态并隐藏船舶图层
      if (this.shipLayerStateBeforeTrack === null) {
        this.shipLayerStateBeforeTrack = this.layerStatus.ship;
        console.log('保存显示航迹前的船舶图层状态:', this.shipLayerStateBeforeTrack);
        
        // 如果船舶图层当前是显示状态，则隐藏它
        if (this.layerStatus.ship) {
          this.layerStatus.ship = false;
          this.toggleShipVisibility(false);
          console.log('隐藏船舶图层以显示航迹');
        }
      }
      
      // 显示航迹查看器
      this.showTrackViewer = true;
      
      // 将处理后的轨迹数据传递给ShipTrackViewer
      // 注意：这里不再调用showShipTrack方法，避免重复刷新
      this.$nextTick(() => {
        if (this.$refs.trackViewer) {
          this.$refs.trackViewer.loadTrajectoryDataFromResponse(latestData);
          console.log(`轨迹数据已直接加载到轨迹查看器，船舶: ${mmsi}，数据条数: ${latestData.length}`);
        } else {
          console.warn('轨迹查看器组件未找到，无法加载轨迹数据');
        }
      });
      
      console.log(`开始显示船舶 ${mmsi} 的轨迹数据，使用最后${latestData.length}条记录`);
    },
    
    /**
     * 格式化日期时间
     * @param {Date} date - 日期对象
     * @returns {string} 格式化后的日期时间字符串
     */
    formatDateTime(date) {
      if (!date || !(date instanceof Date)) {
        return '';
      }
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      const second = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },
    
    /**
     * 处理轮播图变化
     */
    onCarouselChange(current) {
      if (this.recentActiveAlerts.length === 0) return;
      
      const currentAlert = this.recentActiveAlerts[current];
      if (!currentAlert) return;
      
      // 初始化播报次数
      if (!currentAlert.playCount) {
        currentAlert.playCount = 0;
      }
      
      // 增加播报次数
      currentAlert.playCount++;
      
      console.log(`【告警轮播】告警 ${currentAlert.id} 第 ${currentAlert.playCount} 次播报`);
      
      // 如果播报次数达到2次，标记为待删除
      if (currentAlert.playCount >= 2) {
        console.log(`【告警轮播】告警 ${currentAlert.id} 播报完成，准备删除`);
        
        // 延迟删除，让当前播报完成
        setTimeout(() => {
          this.removeAlert(currentAlert);
        }, 500);
      }
    },
    
    /**
     * 清理过期告警（超过一定时间的告警自动清理）
     */
    cleanupExpiredAlerts() {
      const now = Date.now();
      const maxAge = 30 * 60 * 1000; // 30分钟
      
      const expiredAlerts = this.activeAlerts.filter(alert => {
        const alertTime = new Date(alert.alertTime || alert.time || 0).getTime();
        return (now - alertTime) > maxAge;
      });
      
      if (expiredAlerts.length > 0) {
        console.log(`【告警清理】清理 ${expiredAlerts.length} 条过期告警`);
        expiredAlerts.forEach(alert => {
          this.removeAlert(alert);
        });
      }
    },

    /**
     * 启动告警清理定时器
     */
    startAlertCleanupTimer() {
      if (this.alertCleanupTimer) {
        clearInterval(this.alertCleanupTimer);
      }
      this.alertCleanupTimer = setInterval(() => {
        this.cleanupExpiredAlerts();
      }, 5 * 60 * 1000); // 每5分钟检查一次
      console.log('告警清理定时器已启动，每5分钟检查一次');
    },

    /**
     * 停止告警清理定时器
     */
    stopAlertCleanupTimer() {
      if (this.alertCleanupTimer) {
        clearInterval(this.alertCleanupTimer);
        this.alertCleanupTimer = null;
        console.log('告警清理定时器已停止');
      }
    },

    /**
     * 清理全局备用删除定时器
     */
    clearAllBackupTimers() {
      if (this.carouselBackupTimer) {
        clearTimeout(this.carouselBackupTimer);
        this.carouselBackupTimer = null;
        console.warn(`【备用定时器】组件销毁时清理全局备用删除定时器`);
      }
    },

    /**
     * 处理桥梁预警规则弹窗关闭
     */
    handleBridgeAlertRulesClose() {
      this.bridgeAlertRulesVisible = false;
      this.bringPanelToFront('bridgeRules');
    },

    /**
     * 更新桥梁预警规则面板的活跃标签页
     */
    updateBridgeRulesActiveTab(tab) {
      this.bridgeRulesActiveTab = tab;
    }
  }
}
</script>

<style scoped>
.map-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: relative;
}

.map-content {
  flex: 1;
  position: relative;
}

.radar-popup {
  min-width: 200px;
}

/* 告警走马灯样式 */
.alert-carousel-container {
  position: absolute;
  top: 10px;
  left: 45%;
  transform: translateX(-50%);
  z-index: 1000;
  width: 60%;
  max-width: 720px;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.3);
  overflow: hidden;
  border: 1px solid rgba(255, 77, 79, 0.5);
}

.alert-carousel {
  width: 100%;
}

.alert-message {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0 15px;
  color: #ff4d4f;
  font-weight: bold;
  position: relative;
  overflow: hidden;
}

.alert-message i.el-icon-warning-outline {
  margin-right: 10px;
  font-size: 18px;
  color: #ff4d4f;
  flex-shrink: 0;
}

.alert-message span {
  white-space: nowrap;
  display: inline-block;
  animation: marquee 15s linear infinite;
  padding-right: 50px; /* 确保文字完整滚动出视野 */
}

.alert-info {
  display: flex;
  align-items: center;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  gap: 8px;
}



.alert-close {
  margin-left: 0;
  cursor: pointer;
  color: #ffffff;
  opacity: 0.8;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.alert-close:hover {
  opacity: 1;
  color: #ff4d4f;
  background-color: rgba(255, 77, 79, 0.2);
}

/* 走马灯动画 */
@keyframes marquee {
  0% { transform: translateX(100%); }
  100% { transform: translateX(-120%); }
}

/* 告警渐入渐出动画 */
.alert-fade-enter-active, .alert-fade-leave-active {
  transition: opacity 0.5s, transform 0.5s;
}

.alert-fade-enter, .alert-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}

/* 会遇双船航迹信息卡 */
.meeting-tracks-card {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  width: 460px;
  max-width: 70%;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.22);
  overflow: hidden;
  font-size: 12px;
}

.meeting-tracks-card .mt-head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #1e3a8a;
  color: #fff;
  font-weight: 500;
}

.meeting-tracks-card .mt-close {
  margin-left: auto;
  cursor: pointer;
  opacity: 0.85;
}

.meeting-tracks-card .mt-close:hover {
  opacity: 1;
  color: #ff4d4f;
}

.meeting-tracks-card .mt-body {
  padding: 8px 10px;
}

.meeting-tracks-card .mt-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
}

.meeting-tracks-card .mt-badge {
  display: inline-block;
  width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  border-radius: 3px;
  color: #fff;
  font-size: 11px;
  flex: 0 0 auto;
}

.meeting-tracks-card .mt-badge-a {
  background: #e63946;
}

.meeting-tracks-card .mt-badge-b {
  background: #1e88e5;
}

.meeting-tracks-card .mt-name {
  color: #1e3a8a;
  font-weight: 600;
  flex: 0 0 auto;
}

.meeting-tracks-card .mt-meta {
  color: #606266;
}

.meeting-tracks-card .mt-time {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px dashed #ebeef5;
  color: #909399;
  font-size: 11px;
}
</style>

<style>
/* 全局区域标签样式 - 与BridgeMap.vue保持一致 */
.area-label {
  background-color: rgba(0, 0, 0, 0.6) !important;
  border: none !important;
  box-shadow: none !important;
  color: white !important;
  font-weight: bold !important;
  padding: 3px 8px !important;
  border-radius: 4px !important;
  font-size: 12px !important;
  white-space: nowrap !important;
  text-align: center !important;
}

/* 绘制中的区域标签样式 */
.area-label-drawing {
  background-color: rgba(255, 153, 0, 0.8) !important;
  border: 1px dashed white !important;
}

/* 预警船舶图标闪烁动画 */
@keyframes alert-blink {
  0%, 100% { opacity: 1; filter: hue-rotate(0deg); }
  50% { opacity: 0.4; filter: hue-rotate(-30deg); }
}
.ship-icon-alert {
  animation: alert-blink 1s ease-in-out infinite;
}
</style> 